import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import {
  IanaTimeZoneName,
  TIMEZONES,
} from "@redotech/redo-common-model/timezone-list";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { TimeOfDay } from "@redotech/redo-web/arbiter-components/time/redo-time-picker";
import { LocalDateTime } from "@redotech/zod-util/db-coercion";

/**
 * Extract date and time components from a Date in a specific timezone.
 * Use this instead of Date.getHours() etc. which use browser-local timezone.
 */
export function extractDateTimeInTimezone(
  date: Date,
  timezone: IanaTimeZoneName,
): { date: Temporal.PlainDate; time: TimeOfDay } {
  const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
  const zdt = instant.toZonedDateTimeISO(timezone);
  return {
    date: zdt.toPlainDate(),
    time: { hours: zdt.hour, minutes: zdt.minute },
  };
}

/**
 * Parse initial schedule props into date and time components.
 */
export function parseInitialSchedule(
  initialScheduledAtLocal: { localTime: LocalDateTime } | null | undefined,
  initialScheduledAt: Date | undefined,
  initialScheduledAtTimeZone: IanaTimeZoneName | null | undefined,
  browserTimeZone: IanaTimeZoneName,
): { date: Temporal.PlainDate; time: TimeOfDay | null } {
  // Recipient timezone mode: parse from LocalDateTime string
  if (initialScheduledAtLocal) {
    const [datePart, timePart] = initialScheduledAtLocal.localTime.split("T");
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const [hours, minutes] = timePart!.split(":").map(Number);
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      date: Temporal.PlainDate.from(datePart!),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      time: { hours: hours!, minutes: minutes! },
    };
  }

  // Normal mode: extract from Date using the stored timezone
  if (initialScheduledAt) {
    const tz = initialScheduledAtTimeZone ?? browserTimeZone;
    return extractDateTimeInTimezone(initialScheduledAt, tz);
  }

  // No initial schedule
  return { date: Temporal.Now.plainDateISO(), time: null };
}

/**
 * Format a Date for display as a safe delivery time suggestion.
 * Uses the specified timezone for formatting.
 */
export function formatSafeDeliveryTime(
  date: Date,
  timezone: IanaTimeZoneName,
): string {
  const { date: safeDate } = extractDateTimeInTimezone(date, timezone);
  const { date: nowDate } = extractDateTimeInTimezone(new Date(), timezone);

  const isToday = safeDate.equals(nowDate);
  const isTomorrow = safeDate.equals(nowDate.add({ days: 1 }));

  const timeString = date.toLocaleTimeString("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

  if (isToday) {
    return `Today, ${timeString}`;
  } else if (isTomorrow) {
    return `Tomorrow, ${timeString}`;
  } else {
    return date.toLocaleString("en-US", {
      timeZone: timezone,
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  }
}

/**
 * Check if a timezone is supported by the browser's Intl API.
 */
export function isTimezoneSupported(timezone: IanaTimeZoneName): boolean {
  try {
    Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Filter timezone options based on a search string.
 */
export function filterTimezoneOptions(
  options: TextRedoListItem<IanaTimeZoneName>[],
  searchString: string,
): TextRedoListItem<IanaTimeZoneName>[] {
  if (!searchString.trim()) {
    return options;
  }
  const normalize = (value: string) =>
    value.toLowerCase().replace(/[_/\s-]/g, "");
  const search = normalize(searchString);
  return options.filter((option) => normalize(option.text).includes(search));
}

/**
 * Format a TimeOfDay as a time string for display (e.g., "3:45 PM").
 */
export function formatTimeOfDay(time: TimeOfDay): string {
  const hours12 = time.hours % 12 || 12;
  const ampm = time.hours >= 12 ? "PM" : "AM";
  const minutes = time.minutes.toString().padStart(2, "0");
  return `${hours12}:${minutes} ${ampm}`;
}

/**
 * Build a LocalDateTime string from a Temporal.PlainDate and TimeOfDay.
 */
export function buildLocalTimeString(
  date: Temporal.PlainDate,
  time: TimeOfDay,
): LocalDateTime {
  const hours = String(time.hours).padStart(2, "0");
  const minutes = String(time.minutes).padStart(2, "0");
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return `${date}T${hours}:${minutes}:00` as LocalDateTime;
}

/**
 * Check if an instant (local date-time in a specific timezone) has passed.
 * For normal mode where all recipients receive at the same instant.
 */
export function hasInstantPassed(
  dateTime: Temporal.PlainDateTime,
  timezone: IanaTimeZoneName,
): boolean {
  const zonedDateTime = dateTime.toZonedDateTime(timezone);
  const now = Temporal.Now.instant();
  return Temporal.Instant.compare(zonedDateTime.toInstant(), now) <= 0;
}

/**
 * Check if a local date-time has passed in ALL timezones.
 * For recipient timezone mode - returns true only if it's too late everywhere.
 * Checks against UTC-12 (last timezone to reach any local time).
 */
export function hasLocalDateTimePassedInAllTimeZones(
  dateTime: Temporal.PlainDateTime,
): boolean {
  const lastToReachTime = new Date(dateTime.toString() + "-12:00");
  return lastToReachTime < new Date();
}

/**
 * Generate time preset options as TimeOfDay values.
 * Filters out times that have already passed.
 *
 * For normal mode: filters based on the specified timezone.
 * For recipient timezone mode: filters based on UTC-12 (last timezone to reach
 * any local time), showing all times still valid for at least one recipient.
 */
export function getTimePresets(
  sendDate: Temporal.PlainDate,
  channel: MarketingChannel,
  timezone: IanaTimeZoneName,
  useRecipientTimezones?: boolean,
): TextRedoListItem<TimeOfDay>[] {
  const options: TextRedoListItem<TimeOfDay>[] = [];

  // For recipient timezone mode, use UTC-12 (Etc/GMT+12 in IANA notation)
  // to show all times still valid somewhere in the world
  const effectiveTimezone: IanaTimeZoneName = useRecipientTimezones
    ? ("Etc/GMT+12" as IanaTimeZoneName)
    : timezone;

  const nowInTz = Temporal.Now.zonedDateTimeISO(effectiveTimezone);
  const todayInTz = nowInTz.toPlainDate();
  const isToday = sendDate.equals(todayInTz);

  const startHourBase = channel === MarketingChannel.SMS ? 9 : 0;
  const endHour = channel === MarketingChannel.SMS ? 20 : 24;

  let startHour = startHourBase;
  let startMinute = 0;

  if (isToday) {
    const nextMinute = Math.ceil(nowInTz.minute / 15) * 15;

    if (nextMinute >= 60) {
      startHour = nowInTz.hour + 1;
      startMinute = 0;
    } else {
      startHour = nowInTz.hour;
      startMinute = nextMinute;
    }

    if (startHour < startHourBase) {
      startHour = startHourBase;
      startMinute = 0;
    }
  }

  for (let hour = startHour; hour < endHour; hour++) {
    const minStart = hour === startHour ? startMinute : 0;
    for (let min = minStart; min < 60; min += 15) {
      const time: TimeOfDay = { hours: hour, minutes: min };
      options.push({
        id: `${hour}:${min}`,
        type: "text",
        text: formatTimeOfDay(time),
        value: time,
      });
    }
  }

  return options;
}

/**
 * Check if a clock time has passed in a specific timezone.
 */
export function hasClockTimePassedInTimezone(
  date: Temporal.PlainDate,
  time: TimeOfDay,
  timezone: IanaTimeZoneName,
): boolean {
  try {
    const dateTime = date.toPlainDateTime({
      hour: time.hours,
      minute: time.minutes,
    });
    const nowInTz = Temporal.Now.zonedDateTimeISO(timezone).toPlainDateTime();
    return Temporal.PlainDateTime.compare(dateTime, nowInTz) <= 0;
  } catch {
    return false;
  }
}

/**
 * Timezone dropdown options, filtered for browser support and deduplicated.
 */
export const TIMEZONE_OPTIONS: TextRedoListItem<IanaTimeZoneName>[] = (() => {
  const flattened = TIMEZONES.flatMap((tz) =>
    tz.utc.map((utcName) => ({
      id: utcName,
      type: "text" as const,
      text: `${utcName} (UTC${tz.offset >= 0 ? "+" : ""}${tz.offset}:00)`,
      value: utcName,
    })),
  );

  const seen = new Set<string>();
  return flattened.filter((option) => {
    if (seen.has(option.value)) {
      return false;
    }
    seen.add(option.value);
    return isTimezoneSupported(option.value);
  });
})();
